/**
 * @cafe-os/observability — Sentry init helper для всех React apps Orda OS.
 *
 * Pattern: каждое приложение в своём main.tsx вызывает initSentry({app: "..."})
 * до ReactDOM.render. Без DSN в env — функция тихо no-op (для dev / preview).
 *
 * Tags: app + tenant + environment автоматически клеятся ко всем событиям —
 * в Sentry UI можно фильтровать "show me errors from cashier app of tenant alem".
 */
import * as Sentry from "@sentry/react";
import { applyDomainErrorPolicy, shouldInitSentry } from "./sentryPolicy";

export interface InitSentryOptions {
  /** Какое приложение шлёт ошибки — guest/cashier/kitchen/waiter/admin/super-admin/booking/landing. */
  app: string;
  /** Tenant slug если известен на момент init (для landing/super-admin может быть undefined). */
  tenantId?: string;
  /** Release version — обычно git SHA из VITE_GIT_SHA. */
  release?: string;
  /** Включить Session Replay (записывает действия гостя перед крахом). По умолчанию off. */
  enableReplay?: boolean;
  /** Дополнительные tag'и для всех событий. */
  extraTags?: Record<string, string>;
}

/**
 * Initialize Sentry. Call once at app startup перед любым другим кодом.
 *
 * Безопасно вызывать без DSN — функция тихо ничего не делает (для dev / staging
 * где Sentry не настроен).
 */
export function initSentry(options: InitSentryOptions): void {
  const viteEnv =
    (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env) ||
    {};
  const dsn = viteEnv.VITE_SENTRY_DSN || "";
  const env = viteEnv.MODE || "development";
  const devOptIn = viteEnv.VITE_SENTRY_DEV === "1";

  if (!shouldInitSentry({ dsn, mode: env, devOptIn })) {
    // Тихо no-op: либо DSN не настроен, либо это локальная разработка.
    // Поднять события из dev — VITE_SENTRY_DEV=1 в .env.local.
    console.info(
      `[observability:${options.app}] Sentry disabled (${dsn ? `mode=${env}` : "no VITE_SENTRY_DSN"})`,
    );
    return;
  }

  const release =
    options.release ||
    (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_GIT_SHA) ||
    undefined;

  Sentry.init({
    dsn,
    environment: env,
    release,

    // Production-ready defaults. Если ОЧЕНЬ хочется больше событий — менять
    // в Sentry dashboard, не в коде.
    tracesSampleRate: env === "production" ? 0.1 : 1.0,   // 10% requests в prod
    replaysSessionSampleRate: 0,                            // не пишем все сессии
    replaysOnErrorSampleRate: options.enableReplay ? 1.0 : 0, // только если есть ошибка

    // Не шлём PII по умолчанию (телефоны гостей, email и т.д.). Если надо
    // конкретное поле — захватываем явно через setExtra.
    sendDefaultPii: false,

    // Игнорируем известный шум.
    ignoreErrors: [
      // Network errors не наша вина (юзер ушёл в туннель)
      "NetworkError",
      "Failed to fetch",
      "Load failed",
      // BFCache restore (мы намеренно работаем с this)
      "BFCache",
      // Browser extensions injecting scripts
      /chrome-extension/,
      /moz-extension/,
      // iOS in-app browsers may inject a bridge script that assumes a native
      // WKWebView message handler. It is not shipped by Cafe OS.
      /window\.webkit\.messageHandlers/,
      // ResizeObserver loop безвреден
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Chunk-load self-heal — НЕ баг: штатная авто-перезагрузка за свежим кодом
      // после деплоя (installChunkReloadGuard сам reload-ит, captureMessage у него
      // только в этом штатном случае — «AGAIN after reload» идёт лишь в console).
      // Не создаём Sentry-issue → не алертим владельца ложно (фидбек: «в Telegram
      // пришло уведомление об ошибке» при заходе в раздел после деплоя).
      /chunk load failed/i,
    ],

    // Ожидаемые доменные DbError (USER_INPUT_CODES → DbError.isExpected),
    // просочившиеся мимо фильтра callRpc (unhandled rejection) — level:info,
    // чтобы не засорять очередь ошибок. См. applyDomainErrorPolicy.
    beforeSend(event, hint) {
      applyDomainErrorPolicy(event, hint?.originalException);
      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
      ...(options.enableReplay
        ? [
            Sentry.replayIntegration({
              maskAllText: true,      // маскируем весь текст (PII safety)
              maskAllInputs: true,    // маскируем input fields
              blockAllMedia: true,    // не пишем изображения
            }),
          ]
        : []),
    ],
  });

  // Tag everything с app/tenant/env — для фильтрации в Sentry UI.
  Sentry.setTag("app", options.app);
  if (options.tenantId) Sentry.setTag("tenant", options.tenantId);
  Sentry.setTag("environment", env);

  if (options.extraTags) {
    for (const [k, v] of Object.entries(options.extraTags)) {
      Sentry.setTag(k, v);
    }
  }

  console.info(`[observability:${options.app}] Sentry initialized (env=${env}, release=${release ?? "n/a"})`);
}

/**
 * Вручную капчурить ошибку (например в catch блоке когда хочется добавить контекст).
 *
 * `level` — для случаев, когда событие стоит видеть, но оно не «ошибка»:
 * например обрыв realtime у гостя, чей телефон сам заморозил вкладку
 * (см. packages/db/src/realtimeReport.ts). Без явного level остаётся `error`.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
  level?: "info" | "warning" | "error",
): void {
  if (context || level) {
    Sentry.withScope((scope) => {
      if (level) scope.setLevel(level);
      for (const [k, v] of Object.entries(context ?? {})) {
        scope.setExtra(k, v);
      }
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Вручную сообщить о message (не ошибка, а notable event).
 */
export function captureMessage(message: string, level: "info" | "warning" | "error" = "info"): void {
  Sentry.captureMessage(message, level);
}

/**
 * Set user context — например после auth.
 * Sentry shows эти поля в issue UI → видно кто именно столкнулся с ошибкой.
 *
 * Расширенные поля (tenant, role, staff_id) попадают в `user` через rest spread
 * и становятся видны как additional context в Sentry issue UI.
 */
export function setUserContext(user: {
  id?: string;
  email?: string;
  tenant?: string;
  role?: string;
  staff_id?: string;
}): void {
  Sentry.setUser(user);
}

/**
 * Clear user context — например после logout.
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}
